import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "antd";
import PropTypes from "prop-types";
import { BASE_CRM_URL } from "../../../components/config/config";

const getPercentage = (value, total) => {
  if (!total) return 0;

  return Number(((value / total) * 100).toFixed(2));
};

const OverviewRow = ({ label, value, total, progressClass }) => {
  const percentage = getPercentage(value, total);

  return (
    <div className="d-flex row justify-content-between px-3 py-1">
      <div className="col-6">
        {value} {label}
      </div>

      <div className="col-6 text-end">{percentage}%</div>

      <div className="col-12">
        <div
          className="progress progress-sm mb-3"
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`progress-bar ${progressClass}`}
            style={{
              width: `${percentage}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

OverviewRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  progressClass: PropTypes.string.isRequired,
};

const OverviewRowSkeleton = () => {
  return (
    <div className="px-3 py-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Skeleton.Input active size="small" style={{ width: 110 }} />

        <Skeleton.Input active size="small" style={{ width: 45 }} />
      </div>

      <Skeleton.Input
        active
        size="small"
        block
        style={{
          height: 8,
          minHeight: 8,
        }}
      />
    </div>
  );
};

const OverView = () => {
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [invoiceOverview, setInvoiceOverview] = useState({
    total: 0,
    draft: 0,
    notSet: 0,
    unpaid: 0,
    partiallyPaid: 0,
    overdue: 0,
    paid: 0,
  });

  const [estimateOverview, setEstimateOverview] = useState({
    total: 0,
    draft: 0,
    notSet: 0,
    sent: 0,
    expired: 0,
    declined: 0,
    accepted: 0,
  });

  const [proposalOverview, setProposalOverview] = useState({
    total: 0,
    draft: 0,
    sent: 0,
    open: 0,
    revised: 0,
    declined: 0,
    accepted: 0,
  });

  const getDashboardOverview = async () => {
    try {
      setOverviewLoading(true);

      const response = await fetch(
        `${BASE_CRM_URL}dashboard/GetDashboardOverview`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data?.Status && data?.ResultData) {
        const result = data.ResultData;

        setInvoiceOverview({
          total: result?.Invoices?.Total ?? 0,
          draft: result?.Invoices?.Draft ?? 0,
          notSet: result?.Invoices?.NotSet ?? 0,
          unpaid: result?.Invoices?.Unpaid ?? 0,
          partiallyPaid: result?.Invoices?.PartiallyPaid ?? 0,
          overdue: result?.Invoices?.Overdue ?? 0,
          paid: result?.Invoices?.Paid ?? 0,
        });

        setEstimateOverview({
          total: result?.Estimates?.Total ?? 0,
          draft: result?.Estimates?.Draft ?? 0,
          notSet: result?.Estimates?.NotSet ?? 0,
          sent: result?.Estimates?.Sent ?? 0,
          expired: result?.Estimates?.Expired ?? 0,
          declined: result?.Estimates?.Declined ?? 0,
          accepted: result?.Estimates?.Accepted ?? 0,
        });

        setProposalOverview({
          total: result?.Proposal?.Total ?? 0,
          draft: result?.Proposal?.Draft ?? 0,
          sent: result?.Proposal?.Sent ?? 0,
          open: result?.Proposal?.Open ?? 0,
          revised: result?.Proposal?.Revised ?? 0,
          declined: result?.Proposal?.Declined ?? 0,
          accepted: result?.Proposal?.Accepted ?? 0,
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard overview:", error);
    } finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    getDashboardOverview();
  }, []);

  return (
    <div className="col-xl-4 col-sm-12 col-12 col-lg-4 d-flex">
      <div className="card flex-fill">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="d-inline-flex align-items-center">
            <span className="title-icon bg-soft-orange fs-16 me-2">
              <i className="ti ti-chart-line" />
            </span>

            <h5 className="card-title mb-0">Overview</h5>
          </div>
        </div>
        <div className="card-body p-0">
          <ul className="nav nav-tabs nav-justified transaction-tab mb-2">
            <li className="nav-item">
              <Link className="nav-link active" to="#sale" data-bs-toggle="tab">
                Invoice
              </Link>
            </li>
            <li className="nav-item">
              <Link
                className="nav-link"
                to="#purchase-transaction"
                data-bs-toggle="tab"
              >
                Estimate
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="#quotation" data-bs-toggle="tab">
                Proposal
              </Link>
            </li>
          </ul>
          <div className="tab-content">
            <div className="tab-pane show active" id="sale">
              {overviewLoading ? (
                <>
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                </>
              ) : (
                <>
                  <OverviewRow
                    label="Draft"
                    value={invoiceOverview.draft}
                    total={invoiceOverview.total}
                    progressClass="bg-secondary"
                  />

                  <OverviewRow
                    label="Not Set"
                    value={invoiceOverview.notSet}
                    total={invoiceOverview.total}
                    progressClass="bg-teal"
                  />

                  <OverviewRow
                    label="Unpaid"
                    value={invoiceOverview.unpaid}
                    total={invoiceOverview.total}
                    progressClass="bg-info"
                  />

                  <OverviewRow
                    label="Partially Paid"
                    value={invoiceOverview.partiallyPaid}
                    total={invoiceOverview.total}
                    progressClass="bg-warning"
                  />

                  <OverviewRow
                    label="Overdue"
                    value={invoiceOverview.overdue}
                    total={invoiceOverview.total}
                    progressClass="bg-danger"
                  />

                  <OverviewRow
                    label="Paid"
                    value={invoiceOverview.paid}
                    total={invoiceOverview.total}
                    progressClass="bg-success"
                  />
                </>
              )}
            </div>
            <div className="tab-pane fade" id="purchase-transaction">
              {overviewLoading ? (
                <>
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                </>
              ) : (
                <>
                  <OverviewRow
                    label="Draft"
                    value={estimateOverview.draft}
                    total={estimateOverview.total}
                    progressClass="bg-secondary"
                  />

                  <OverviewRow
                    label="Not Set"
                    value={estimateOverview.notSet}
                    total={estimateOverview.total}
                    progressClass="bg-teal"
                  />

                  <OverviewRow
                    label="Sent"
                    value={estimateOverview.sent}
                    total={estimateOverview.total}
                    progressClass="bg-info"
                  />

                  <OverviewRow
                    label="Expired"
                    value={estimateOverview.expired}
                    total={estimateOverview.total}
                    progressClass="bg-warning"
                  />

                  <OverviewRow
                    label="Declined"
                    value={estimateOverview.declined}
                    total={estimateOverview.total}
                    progressClass="bg-danger"
                  />

                  <OverviewRow
                    label="Accepted"
                    value={estimateOverview.accepted}
                    total={estimateOverview.total}
                    progressClass="bg-success"
                  />
                </>
              )}
            </div>
            <div className="tab-pane" id="quotation">
              {overviewLoading ? (
                <>
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                  <OverviewRowSkeleton />
                </>
              ) : (
                <>
                  <OverviewRow
                    label="Draft"
                    value={proposalOverview.draft}
                    total={proposalOverview.total}
                    progressClass="bg-secondary"
                  />

                  <OverviewRow
                    label="Sent"
                    value={proposalOverview.sent}
                    total={proposalOverview.total}
                    progressClass="bg-teal"
                  />

                  <OverviewRow
                    label="Open"
                    value={proposalOverview.open}
                    total={proposalOverview.total}
                    progressClass="bg-info"
                  />

                  <OverviewRow
                    label="Revised"
                    value={proposalOverview.revised}
                    total={proposalOverview.total}
                    progressClass="bg-warning"
                  />

                  <OverviewRow
                    label="Declined"
                    value={proposalOverview.declined}
                    total={proposalOverview.total}
                    progressClass="bg-danger"
                  />

                  <OverviewRow
                    label="Accepted"
                    value={proposalOverview.accepted}
                    total={proposalOverview.total}
                    progressClass="bg-success"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverView;
